import { Button, Container, Label, NumericInput, SelectInput, VectorInput } from '@playcanvas/pcui';
import { Vec3 } from 'playcanvas';

import { Events } from '../events';
import type { OrbitParams, OrbitDirection, EasePreset } from '../orbit-generator';

type OrbitDialogResult = OrbitParams | null;

class OrbitDialog extends Container {
    show: (defaults?: Partial<OrbitParams>) => Promise<OrbitDialogResult>;
    hide: () => void;
    destroy: () => void;

    constructor(events: Events, args = {}) {
        args = {
            ...args,
            id: 'orbit-dialog',
            class: 'settings-dialog',
            hidden: true,
            tabIndex: -1
        };

        super(args);

        this.dom.setAttribute('role', 'dialog');
        this.dom.setAttribute('aria-modal', 'true');

        const dialog = new Container({ id: 'dialog' });

        // header
        const headerText = new Label({ id: 'text', text: 'GENERATE ORBIT' });
        const header = new Container({ id: 'header' });
        header.append(headerText);

        // content
        const content = new Container({ id: 'content' });

        const makeRow = (labelText: string, control: any): Container => {
            const label = new Label({ class: 'label', text: labelText });
            const row = new Container({ class: 'row' });
            row.append(label);
            row.append(control);
            return row;
        };

        // Center X/Y/Z
        const centerInput = new VectorInput({
            class: 'vector-input',
            dimensions: 3,
            precision: 2,
            step: 0.5,
            value: [0, 0, 0]
        });

        const centerButtonRow = new Container({ class: 'row' });
        centerButtonRow.dom.style.justifyContent = 'flex-end';
        centerButtonRow.dom.style.gap = '4px';
        centerButtonRow.dom.style.paddingTop = '0';

        const useSelectionBtn = new Button({ class: 'button', text: 'Use Selection' });
        const useViewCenterBtn = new Button({ class: 'button', text: 'Use View Center' });
        centerButtonRow.append(useSelectionBtn);
        centerButtonRow.append(useViewCenterBtn);

        const setCenterFromSelection = () => {
            const sel = events.invoke('selection') as any;
            let pt: Vec3 | null = null;
            let bound: any = null;

            if (sel && typeof sel.focalPoint === 'function') {
                pt = sel.focalPoint();
            } else if (sel?.worldBound) {
                pt = sel.worldBound.center;
                bound = sel.worldBound;
            }

            if (!pt) {
                events.invoke('showPopup', {
                    type: 'info',
                    header: 'No Selection',
                    message: 'Select a splat or mesh in the scene first.'
                });
                return;
            }

            centerInput.value = [pt.x, pt.y, pt.z];

            // Auto-suggest radius from bounding box size
            if (!bound && sel?.worldBound) bound = sel.worldBound;
            if (bound) {
                const extents = bound.halfExtents;
                const suggestedRadius = Math.max(0.5, Math.sqrt(extents.x * extents.x + extents.z * extents.z) * 2);
                radiusInput.value = Math.round(suggestedRadius * 100) / 100;
            }
        };

        useSelectionBtn.on('click', setCenterFromSelection);

        useViewCenterBtn.on('click', () => {
            const pose = events.invoke('camera.getPose') as { target: { x: number; y: number; z: number } } | null;
            if (pose?.target) {
                centerInput.value = [pose.target.x, pose.target.y, pose.target.z];
            }
        });

        // Radius
        const radiusInput = new NumericInput({
            class: 'text-input',
            value: 5,
            min: 0.1,
            precision: 2,
            step: 0.5
        });

        // Elevation (degrees)
        const elevationInput = new NumericInput({
            class: 'text-input',
            value: 15,
            min: -89,
            max: 89,
            precision: 1,
            step: 5
        });

        // Revolutions
        const revolutionsInput = new NumericInput({
            class: 'text-input',
            value: 1,
            min: 0.25,
            max: 20,
            precision: 2,
            step: 0.25
        });

        // Direction
        const directionSelect = new SelectInput({
            class: 'select',
            defaultValue: 'ccw',
            options: [
                { v: 'ccw', t: 'Counter-Clockwise' },
                { v: 'cw', t: 'Clockwise' }
            ]
        });

        // Points per revolution
        const pointsInput = new NumericInput({
            class: 'text-input',
            value: 12,
            min: 4,
            max: 64,
            precision: 0,
            step: 4
        });

        // Ease preset
        const easeSelect = new SelectInput({
            class: 'select',
            defaultValue: 'linear',
            options: [
                { v: 'linear', t: 'Linear' },
                { v: 'easeIn', t: 'Ease In' },
                { v: 'easeOut', t: 'Ease Out' },
                { v: 'easeInOut', t: 'Ease In-Out' }
            ]
        });

        // Start frame
        const startFrameInput = new NumericInput({
            class: 'text-input',
            value: 0,
            min: 0,
            precision: 0,
            step: 1
        });

        // End frame
        const endFrameInput = new NumericInput({
            class: 'text-input',
            value: 150,
            min: 1,
            precision: 0,
            step: 10
        });

        // Summary label
        const summaryLabel = new Label({ text: '' });
        summaryLabel.dom.style.fontSize = '11px';
        summaryLabel.dom.style.opacity = '0.75';
        summaryLabel.dom.style.padding = '4px 0';

        const updateSummary = () => {
            const revs = revolutionsInput.value as number;
            const pts = pointsInput.value as number;
            const totalKeys = Math.max(2, Math.round(revs * pts)) + 1;
            const startF = startFrameInput.value as number;
            const endF = endFrameInput.value as number;
            const fps = events.invoke('timeline.frameRate') as number;
            const validFps = (typeof fps === 'number' && fps > 0) ? fps : 30;
            const durationSec = Math.abs(endF - startF) / validFps;
            summaryLabel.text = `${totalKeys} keyframes over ${durationSec.toFixed(1)}s`;
        };

        radiusInput.on('change', updateSummary);
        elevationInput.on('change', updateSummary);
        revolutionsInput.on('change', updateSummary);
        pointsInput.on('change', updateSummary);
        startFrameInput.on('change', updateSummary);
        endFrameInput.on('change', updateSummary);

        content.append(makeRow('Center', centerInput));
        content.append(centerButtonRow);
        content.append(makeRow('Radius', radiusInput));
        content.append(makeRow('Elevation (°)', elevationInput));
        content.append(makeRow('Revolutions', revolutionsInput));
        content.append(makeRow('Direction', directionSelect));
        content.append(makeRow('Points / Revolution', pointsInput));
        content.append(makeRow('Easing', easeSelect));
        content.append(makeRow('Start Frame', startFrameInput));
        content.append(makeRow('End Frame', endFrameInput));

        const summaryRow = new Container({ class: 'row' });
        summaryRow.append(summaryLabel);
        content.append(summaryRow);

        // footer
        const footer = new Container({ id: 'footer' });

        const cancelButton = new Button({
            class: 'button',
            text: 'Cancel'
        });

        const generateButton = new Button({
            class: 'button',
            text: 'Generate'
        });

        footer.append(cancelButton);
        footer.append(generateButton);

        dialog.append(header);
        dialog.append(content);
        dialog.append(footer);

        this.append(dialog);

        let onCancel: () => void;
        let onGenerate: () => void;

        cancelButton.on('click', () => onCancel());
        generateButton.on('click', () => onGenerate());

        const keydown = (e: KeyboardEvent) => {
            switch (e.key) {
                case 'Escape':
                    e.preventDefault();
                    e.stopPropagation();
                    onCancel();
                    break;
                case 'Enter':
                    e.preventDefault();
                    e.stopPropagation();
                    onGenerate();
                    break;
                default:
                    e.stopPropagation();
                    break;
            }
        };

        this.show = (defaults?: Partial<OrbitParams>) => {
            // Reset all inputs to baseline, then apply caller overrides.
            // This prevents stale values from a previous session leaking through.
            centerInput.value = [0, 0, 0];
            radiusInput.value = 5;
            elevationInput.value = 15;
            revolutionsInput.value = 1;
            directionSelect.value = 'ccw';
            pointsInput.value = 12;
            easeSelect.value = 'linear';
            startFrameInput.value = 0;
            endFrameInput.value = 150;

            // Apply caller overrides
            if (defaults?.center) {
                centerInput.value = [defaults.center.x, defaults.center.y, defaults.center.z];
            }
            if (defaults?.radius !== undefined) radiusInput.value = defaults.radius;
            if (defaults?.elevation !== undefined) elevationInput.value = defaults.elevation;
            if (defaults?.revolutions !== undefined) revolutionsInput.value = defaults.revolutions;
            if (defaults?.direction !== undefined) directionSelect.value = defaults.direction;
            if (defaults?.pointsPerRevolution !== undefined) pointsInput.value = defaults.pointsPerRevolution;
            if (defaults?.ease !== undefined) easeSelect.value = defaults.ease;
            if (defaults?.startFrame !== undefined) startFrameInput.value = defaults.startFrame;
            if (defaults?.endFrame !== undefined) endFrameInput.value = defaults.endFrame;

            // Set end frame from timeline if not specified
            if (defaults?.endFrame === undefined) {
                const frames = events.invoke('timeline.frames') as number;
                if (typeof frames === 'number' && frames > 0) {
                    endFrameInput.value = frames;
                }
            }

            updateSummary();

            this.hidden = false;
            this.dom.addEventListener('keydown', keydown);
            this.dom.focus();

            return new Promise<OrbitDialogResult>((resolve) => {
                onCancel = () => resolve(null);

                onGenerate = () => {
                    const startF = startFrameInput.value as number;
                    const endF = endFrameInput.value as number;

                    if (endF <= startF) {
                        events.invoke('showPopup', {
                            type: 'error',
                            header: 'Invalid',
                            message: 'End frame must be greater than start frame.'
                        });
                        return;
                    }

                    const cv = centerInput.value as number[];
                    resolve({
                        center: new Vec3(cv[0], cv[1], cv[2]),
                        radius: radiusInput.value as number,
                        elevation: elevationInput.value as number,
                        revolutions: revolutionsInput.value as number,
                        direction: directionSelect.value as OrbitDirection,
                        startFrame: startF,
                        endFrame: endF,
                        pointsPerRevolution: pointsInput.value as number,
                        ease: easeSelect.value as EasePreset
                    });
                };
            }).finally(() => {
                this.dom.removeEventListener('keydown', keydown);
                this.hide();
            });
        };

        this.hide = () => {
            this.hidden = true;
        };

        this.destroy = () => {
            this.hide();
            super.destroy();
        };
    }
}

export { OrbitDialog };
